import clsx from 'clsx'
import PropTypes from 'prop-types'
import { resolveStatusTag } from '@/pages/Submittals/constants'
import {
  CheckHistoryOutlined,
  CheckDoubleOutlined,
  XCircleSolid,
  ClockSolid,
} from '@/assets/svgs/status'

const STATUS_ICON = {
  history: CheckHistoryOutlined,
  'check-double': CheckDoubleOutlined,
  'x-circle': XCircleSolid,
  clock: ClockSolid,
}

// The variant is a role name; this is the only place it becomes a colour. Same
// four roles the listing's tag uses, so a status looks the same on both screens.
const PILL_COLORS = {
  success: 'pill-approved',
  critical: 'pill-rejected',
  warning: 'pill-pending-review',
  neutral: 'pill-neutral',
}

/**
 * A submittal's status as a pill, worded for the data element's own workflow.
 *
 * `secondaryApproval` comes from the data element record: `required` says the
 * element has a second approver, `label` names them. Without it a status reads
 * with its single-stage wording, which is correct for EMS and is what this
 * rendered before the two-stage flow existed.
 *
 * Resolution is delegated to resolveStatusTag rather than kept as local label
 * and colour maps, so this and the listing cannot drift — the previous local
 * maps knew nothing of SECONDARY_APPROVED/SECONDARY_REJECTED and rendered
 * nothing at all for them.
 *
 * Renders nothing for an unrecognised status, as it always has.
 */
const SubmittalStatusPill = ({ status, secondaryApproval }) => {
  const tag = resolveStatusTag(status, secondaryApproval)
  if (!tag) return null

  const Icon = STATUS_ICON[tag.icon]
  return (
    <span
      className={clsx(
        'inline-flex items-center h-6 px-2 whitespace-nowrap rounded-2xl type-meta-heavy',
        PILL_COLORS[tag.variant]
      )}
    >
      {Icon && <Icon className="mr-1 w-4 h-4 shrink-0" />}
      {tag.label}
    </span>
  )
}

SubmittalStatusPill.propTypes = {
  status: PropTypes.string,
  secondaryApproval: PropTypes.shape({
    required: PropTypes.bool,
    label: PropTypes.string,
  }),
}

export default SubmittalStatusPill
